export const INFO_PAGES = {
  "how-it-works": {
    overline: "The Process",
    title: "How Electronics Buyback Works",
    metaTitle: "How Electronics Buyback Works in Chicago",
    metaDescription: "Estimate, prepare, evaluate, get paid. The complete electronics buyback process explained for Chicago sellers — transparent from start to finish.",
    intro: "Buyback is the direct sale of your working device to a business that refurbishes and resells it. No listings, no strangers, no waiting for payment. Here is the entire process.",
    sections: [
      {
        heading: "01 — Get an indicative estimate",
        paragraphs: [
          "Tell us what you have: device category, model, storage, and honest condition. You receive an indicative range — a realistic bracket, not a headline number designed to get you through the door. Any service promising a single guaranteed price without seeing the device is guessing, and the guess usually drops after inspection.",
          "Estimates take under a minute and carry no obligation.",
        ],
      },
      {
        heading: "02 — Prepare your device",
        paragraphs: [
          "Back up your data, sign out of your accounts (iCloud, Google, Samsung, PSN), and erase the device. This protects your privacy and keeps the device eligible: account-locked devices cannot be activated by a new owner, so no legitimate buyer will accept one.",
          "Our Buyback Guides walk through the exact sequence for iPhones, Samsung phones, MacBooks, and PlayStation consoles.",
        ],
        links: [
          { to: "/guides/what-to-do-before-selling-your-iphone", label: "iPhone preparation checklist" },
          { to: "/guides/how-to-prepare-a-macbook-before-selling", label: "MacBook preparation guide" },
        ],
      },
      {
        heading: "03 — In-person evaluation",
        paragraphs: [
          "Bring the device in. It is tested in front of you: power-on, screen, cameras, speakers, charging, buttons, sensors, and battery health. The evaluator confirms model and storage, verifies that accounts are signed out, and checks ownership — a step that protects you as much as the buyer.",
          "Most evaluations take ten to twenty minutes.",
        ],
      },
      {
        heading: "04 — Offer and payment",
        paragraphs: [
          "You receive a final offer based on what the evaluation found, with the reasoning explained line by line. Accept and you are paid on the spot. Decline and you leave with your device. Either outcome is fine — the evaluation itself is free.",
        ],
      },
    ],
  },
  "prepare-your-device": {
    overline: "Preparation",
    title: "Device Preparation Guide",
    metaTitle: "How to Prepare Any Device Before Selling | Chicago Buyback",
    metaDescription: "The universal pre-sale checklist: back up, sign out of accounts, erase, and gather the details buyers price on. Works for phones, laptops, tablets, watches, and consoles.",
    intro: "Every buyback evaluation starts with the same question: is this device ready to change owners? This universal checklist works for every category we buy.",
    sections: [
      {
        heading: "Back up everything first",
        paragraphs: [
          "Nothing is recoverable after an erase. iPhones back up to iCloud or a computer; Samsung and Pixel phones to their Google or Samsung accounts; Macs to Time Machine; consoles to cloud saves or USB. Verify the backup completed before moving on.",
        ],
      },
      {
        heading: "Remove every account lock",
        paragraphs: [
          "Activation Lock (Apple), Factory Reset Protection (Google), Reactivation Lock (Samsung), and PSN console activation all tie a device to you. Each must be removed before the device is erased, or it becomes unsellable. This single step causes more declined evaluations than any other.",
        ],
        links: [
          { to: "/guides/how-to-sign-out-of-icloud-before-selling", label: "Sign out of iCloud" },
          { to: "/guides/how-to-remove-google-account-before-selling-android", label: "Remove a Google account" },
        ],
      },
      {
        heading: "Erase the device",
        paragraphs: [
          "Factory reset only after backup and account removal. Leave the device at its welcome screen — do not set it up again.",
        ],
        links: [
          { to: "/guides/how-to-factory-reset-iphone-before-selling", label: "Reset an iPhone" },
          { to: "/guides/how-to-factory-reset-samsung-before-selling", label: "Reset a Samsung" },
          { to: "/guides/how-to-factory-reset-playstation-before-selling", label: "Reset a PlayStation" },
        ],
      },
      {
        heading: "Know your specs",
        paragraphs: [
          "Model, storage, and battery health set the price. Note them before erasing: Settings → General → About on iPhone, Settings → About phone on Android, Apple menu → About This Mac on a MacBook.",
        ],
      },
      {
        heading: "Present it well",
        paragraphs: [
          "Charge the battery, wipe the device down, and bring chargers, cables, and original packaging if you have them. Presentation will not hide wear, but a complete, clean device evaluates faster and signals careful ownership.",
        ],
      },
    ],
  },
  "data-privacy": {
    overline: "Privacy",
    title: "Data Privacy Before Selling",
    metaTitle: "Data Privacy Before Selling Electronics | What to Remove",
    metaDescription: "Your devices hold banking apps, photos, messages, and passwords. Exactly what to remove — and in what order — before selling any electronics.",
    intro: "A used phone can contain more of your life than your wallet ever did. Selling safely means removing your data in the right order — and verifying it is gone.",
    sections: [
      {
        heading: "What your devices actually hold",
        paragraphs: [
          "Phones and laptops carry saved passwords, banking and payment apps, years of photos and messages, email access, and two-factor authentication codes. Consoles hold payment cards and accounts. Smartwatches hold health data. Treat every device as a set of keys, not a gadget.",
        ],
      },
      {
        heading: "The order matters",
        paragraphs: [
          "Back up first. Sign out of accounts second. Erase third. Erasing before signing out leaves anti-theft locks active and can strand your data or your sale. The sequence is the same across Apple, Google, Samsung, Sony, and Microsoft devices.",
        ],
      },
      {
        heading: "Two-factor authentication",
        paragraphs: [
          "If your phone generates 2FA codes for work, banking, or email, transfer those accounts to your new device before erasing the old one. Losing your 2FA device can lock you out of accounts for days.",
        ],
      },
      {
        heading: "Verify remotely",
        paragraphs: [
          "After the sale, check iCloud.com → Find Devices and myaccount.google.com → Security → Your devices. If a sold device still appears, remove it. A clean device list is your confirmation that nothing of yours travels with the hardware.",
        ],
        links: [
          { to: "/guides/how-to-sign-out-of-icloud-before-selling", label: "Sign out of iCloud" },
          { to: "/guides/how-to-remove-google-account-before-selling-android", label: "Remove a Google account" },
        ],
      },
      {
        heading: "SIMs, eSIMs, and memory cards",
        paragraphs: [
          "Remove physical SIM and microSD cards — they carry contacts and data independent of the device. Transfer your eSIM to your new phone before erasing the old one.",
        ],
      },
    ],
  },
  "chicago-buyback-guide": {
    overline: "Local Chicago",
    title: "The Chicago Electronics Buyback Guide",
    metaTitle: "Chicago Electronics Buyback Guide | Sell Locally with Confidence",
    metaDescription: "Everything about selling electronics locally in Chicago: how buyback counters work, what to bring, how offers are made, and how local compares to shipping.",
    intro: "Selling electronics in Chicago has advantages no mail-in service can match: the device never leaves your sight, the offer is final when you shake on it, and you are paid the same day.",
    sections: [
      {
        heading: "Why local works",
        paragraphs: [
          "Chicago's density means a real, competitive resale market for phones, laptops, tablets, watches, and consoles. Local buyback counters refurbish and resell into that market, which is why working devices in good condition command fair offers — and why broken devices fall outside the model entirely.",
          "An in-person sale also removes the three risks of shipping: transit damage, re-quotes after inspection, and weeks of waiting.",
        ],
        links: [
          { to: "/guides/selling-electronics-locally-vs-shipping-online", label: "Local vs shipping, compared" },
        ],
      },
      {
        heading: "What to bring",
        paragraphs: [
          "The device, charged and factory reset. Its charger and cables. A photo ID — legitimate buyers verify ownership, and you should be suspicious of any that do not. If you have proof of purchase (especially for sealed items), bring it.",
        ],
      },
      {
        heading: "What a legitimate evaluation looks like",
        paragraphs: [
          "The device is tested in front of you, the findings are explained, and the offer follows from them. You are free to decline. Pressure tactics, refusal to test in your presence, or requests to hand over the device before terms are agreed are all red flags.",
        ],
        links: [
          { to: "/guides/how-electronics-buyback-works-in-chicago", label: "How buyback works, step by step" },
        ],
      },
      {
        heading: "Resale versus recycling",
        paragraphs: [
          "Working devices belong in the resale stream — they have years of useful life and real cash value. Devices that genuinely no longer work belong in certified e-waste recycling, which Chicago supports through city drop-off programs. The two streams should never be confused: a buyback counter is for devices with remaining life, not disposal.",
        ],
      },
      {
        heading: "Timing your sale",
        paragraphs: [
          "Phone values dip when new flagship generations launch, because trade-in supply floods the market. Selling in the weeks before an expected launch — rather than the months after — typically means a better offer.",
        ],
      },
    ],
  },
};
